import React, { useState, useEffect, useRef } from 'react';
import { 
  Receipt, 
  ShieldCheck, 
  Building2, 
  Plus, 
  Edit3, 
  RefreshCw, 
  AlertCircle,
  Truck,
  FileCheck,
  UploadCloud,
  Key,
  Calendar,
  CheckCircle2,
  X,
  Trash2,
  Lock,
  Percent,
  Layers,
  FileSpreadsheet,
  Activity,
  Zap,
  Globe,
  Search
} from 'lucide-react';
import { fiscalService } from '../../services/fiscalService';
import { EmpresaFiscal, RegimeTributario, AmbienteFiscal } from '../../types/fiscal';
import { Link } from 'react-router-dom';
import { MunicipioUfSelect } from '../common/MunicipioUfSelect';
import { CfopAutocomplete } from '../common/CfopAutocomplete';
import { buscarDadosCnpj } from '../../lib/cnpjLookup';

export function ConfiguracaoFiscalSection() {
  const [empresas, setEmpresas] = useState<EmpresaFiscal[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ tipo: 'sucesso' | 'erro'; mensagem: string } | null>(null);

  // Estados de Modais
  const [modalEmpresaAberto, setModalEmpresaAberto] = useState(false);
  const [empresaEmEdicao, setEmpresaEmEdicao] = useState<Partial<EmpresaFiscal> | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<'geral' | 'certificado' | 'tributacao' | 'series'>('geral');
  const [salvando, setSalvando] = useState(false);

  // Modal rápido de Certificado Digital
  const [modalCertificadoAberto, setModalCertificadoAberto] = useState(false);
  const [empresaSelecionadaCertificado, setEmpresaSelecionadaCertificado] = useState<EmpresaFiscal | null>(null);
  const [arquivoCertificado, setArquivoCertificado] = useState<File | null>(null);
  const [senhaCertificado, setSenhaCertificado] = useState('');
  const [enviandoCertificado, setEnviandoCertificado] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal e Estado de Teste de Comunicação SEFAZ / Focus
  const [testandoSefaz, setTestandoSefaz] = useState(false);
  const [modalResultadoTeste, setModalResultadoTeste] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState<any>(null);

  // Busca automática de dados na Receita Federal via Focus NFe
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);

  useEffect(() => {
    carregarEmpresas();
  }, []);

  const showFeedback = (tipo: 'sucesso' | 'erro', mensagem: string) => {
    setFeedback({ tipo, mensagem });
    setTimeout(() => setFeedback(null), 4000);
  };

  const carregarEmpresas = async () => {
    setLoading(true);
    try {
      const list = await fiscalService.getEmpresasFiscais();
      setEmpresas(list);
    } catch (e: any) {
      console.error('Erro ao carregar empresas fiscais:', e);
      showFeedback('erro', 'Falha ao sincronizar empresas fiscais.');
    } finally {
      setLoading(false);
    }
  };

  const handleNovaEmpresa = () => {
    setEmpresaEmEdicao({
      razao_social: '',
      nome_fantasia: '',
      cnpj: '',
      inscricao_estadual: '',
      inscricao_municipal: '',
      rntrc: '',
      regime_tributario: 'simples_nacional',
      ambiente_emissao: 'homologacao',
      ativo: true,
      aliquota_icms: 0,
      aliquota_iss: 2.0,
      aliquota_pis: 0,
      aliquota_cofins: 0,
      aliquota_inss: 0,
      aliquota_csll: 0,
      cfop_padrao: '5353',
      cst_icms_padrao: '102',
      reducao_base_icms: 0,
      iss_retido_padrao: false,
      serie_cte: '1',
      proximo_numero_cte: 1,
      serie_nfse: '1',
      proximo_numero_nfse: 1,
      serie_mdfe: '1',
      proximo_numero_mdfe: 1,
    });
    setAbaAtiva('geral');
    setModalEmpresaAberto(true);
  };

  const handleEditarEmpresa = (empresa: EmpresaFiscal) => {
    setEmpresaEmEdicao({ ...empresa });
    setAbaAtiva('geral');
    setModalEmpresaAberto(true);
  };

  const handleSalvarEmpresa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresaEmEdicao || !empresaEmEdicao.razao_social || !empresaEmEdicao.cnpj) {
      showFeedback('erro', 'Razão Social e CNPJ são campos obrigatórios.');
      return;
    }

    setSalvando(true);
    try {
      await fiscalService.saveEmpresaFiscal(empresaEmEdicao);
      showFeedback('sucesso', 'Parâmetros da empresa emissora salvos com sucesso!');
      setModalEmpresaAberto(false);
      setEmpresaEmEdicao(null);
      await carregarEmpresas();
    } catch (err: any) {
      showFeedback('erro', err.message || 'Erro ao salvar empresa emissora.');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluirEmpresa = async (id: number, razao: string) => {
    if (!confirm(`Deseja realmente remover a empresa emissora "${razao}"?`)) return;
    try {
      await fiscalService.deleteEmpresaFiscal(id);
      showFeedback('sucesso', 'Empresa emissora removida.');
      await carregarEmpresas();
    } catch (err: any) {
      showFeedback('erro', 'Erro ao remover empresa emissora.');
    }
  };

  const handleAbrirUploadCertificado = (empresa: EmpresaFiscal) => {
    setEmpresaSelecionadaCertificado(empresa);
    setArquivoCertificado(null);
    setSenhaCertificado('');
    setModalCertificadoAberto(true);
  };

  const handleEnviarCertificado = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresaSelecionadaCertificado) return;
    if (!arquivoCertificado) {
      showFeedback('erro', 'Selecione o arquivo .pfx ou .p12 do Certificado Digital.');
      return;
    }
    if (!senhaCertificado) {
      showFeedback('erro', 'Digite a senha do Certificado Digital.');
      return;
    }

    setEnviandoCertificado(true);
    try {
      await fiscalService.uploadCertificadoDigital(
        empresaSelecionadaCertificado.id,
        arquivoCertificado,
        senhaCertificado
      );
      showFeedback('sucesso', `Certificado Digital A1 vinculado com sucesso à empresa ${empresaSelecionadaCertificado.razao_social}!`);
      setModalCertificadoAberto(false);
      setArquivoCertificado(null);
      setSenhaCertificado('');
      await carregarEmpresas();
    } catch (err: any) {
      showFeedback('erro', err.message || 'Erro ao processar Certificado Digital.');
    } finally {
      setEnviandoCertificado(false);
    }
  };

  const handleBuscarCnpj = async () => {
    if (!empresaEmEdicao?.cnpj) {
      showFeedback('erro', 'Digite o CNPJ antes de buscar.');
      return;
    }
    setBuscandoCnpj(true);
    try {
      const dados = await buscarDadosCnpj(empresaEmEdicao.cnpj);
      if (!dados) {
        showFeedback('erro', 'CNPJ não encontrado na Receita Federal / Focus NFe.');
        return;
      }
      setEmpresaEmEdicao((prev) => ({
        ...prev,
        razao_social: dados.razaoSocial || prev?.razao_social,
        nome_fantasia: dados.nomeFantasia || prev?.nome_fantasia,
        municipio: dados.municipio || prev?.municipio,
        uf: dados.uf || prev?.uf,
        codigo_municipio_ibge: dados.codigoIbge || prev?.codigo_municipio_ibge,
      }));
      showFeedback('sucesso', 'Dados da empresa preenchidos a partir da Receita Federal.');
    } catch (err: any) {
      showFeedback('erro', err.message || 'Erro ao consultar CNPJ.');
    } finally {
      setBuscandoCnpj(false);
    }
  };

  const handleTestarSefaz = async (empresa?: EmpresaFiscal) => {
    setTestandoSefaz(true);
    try {
      const res = await fiscalService.testarComunicacaoFocus(empresa?.id);
      setResultadoTeste({
        ...res,
        empresa: empresa?.razao_social || 'Empresa Principal',
        cnpj: empresa?.cnpj || 'Geral',
      });
      setModalResultadoTeste(true);
      if (res.sucesso) {
        showFeedback('sucesso', 'Comunicação com a Focus NFe / SEFAZ estabelecida com sucesso!');
      } else {
        showFeedback('erro', res.mensagem || 'Falha no teste de comunicação.');
      }
    } catch (err: any) {
      showFeedback('erro', `Erro ao testar comunicação: ${err.message}`);
    } finally {
      setTestandoSefaz(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast de Feedback */}
      {feedback && (
        <div
          className={`p-4 rounded-xl text-xs font-semibold flex items-center justify-between shadow-lg transition-all animate-in fade-in slide-in-from-top-2 ${
            feedback.tipo === 'sucesso'
              ? 'bg-emerald-600 text-white shadow-emerald-900/20'
              : 'bg-red-600 text-white shadow-red-900/20'
          }`}
        >
          <div className="flex items-center gap-2">
            {feedback.tipo === 'sucesso' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-200 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-200 shrink-0" />
            )}
            <span>{feedback.mensagem}</span>
          </div>
          <button onClick={() => setFeedback(null)} className="p-1 hover:bg-white/20 rounded cursor-pointer">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Banner de Visão Geral Focada no Negócio */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm border border-slate-700/50">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 text-2xs font-mono font-bold uppercase tracking-wider">
              Módulo Fiscal Ativo
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-2xs font-bold flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" /> SEFAZ & Emissão Conectados
            </span>
          </div>
          <h2 className="text-xl font-bold tracking-tight">Configurações & Parâmetros Fiscais</h2>
          <p className="text-xs text-slate-300 mt-1 max-w-2xl leading-relaxed">
            Gerencie as empresas emissoras do grupo, parametrize alíquotas de impostos (ICMS, ISS, PIS, COFINS),
            mantenha os Certificados Digitais A1 atualizados e configure séries para emissão de CT-e, MDF-e e NFS-e.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/fiscal"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-md shadow-red-900/30 transition-all hover:scale-102 whitespace-nowrap cursor-pointer"
          >
            <Receipt className="w-4 h-4" />
            Abrir Central Fiscal
          </Link>
        </div>
      </div>

      {/* Indicadores de Prontidão Operacional */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Empresas Habilitadas */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <span className="text-2xs font-bold uppercase tracking-wider text-slate-400 block">Empresas Emissoras</span>
            <span className="text-2xl font-black text-slate-900">{empresas.length}</span>
            <span className="text-2xs text-emerald-600 font-semibold block mt-0.5">Multi-CNPJ Ativo</span>
          </div>
        </div>

        {/* Card 2: Certificados A1 */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <span className="text-2xs font-bold uppercase tracking-wider text-slate-400 block">Certificado Digital A1</span>
            <span className="text-2xl font-black text-slate-900">
              {empresas.filter(e => e.certificado && e.certificado.status === 'valido').length} / {empresas.length}
            </span>
            <span className="text-2xs text-slate-500 font-medium block mt-0.5">Assinatura Eletrônica</span>
          </div>
        </div>

        {/* Card 3: Transporte Rodoviário (CT-e) */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <Truck className="w-6 h-6" />
          </div>
          <div>
            <span className="text-2xs font-bold uppercase tracking-wider text-slate-400 block">CT-e & MDF-e</span>
            <span className="text-sm font-bold text-slate-900">SEFAZ Autorizada</span>
            <span className="text-2xs text-emerald-600 font-semibold block mt-0.5">Mod. 57 / 58 Habilitados</span>
          </div>
        </div>

        {/* Card 4: Serviços Municipais (NFS-e) */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <Receipt className="w-6 h-6" />
          </div>
          <div>
            <span className="text-2xs font-bold uppercase tracking-wider text-slate-400 block">NFS-e Municipal</span>
            <span className="text-sm font-bold text-slate-900">Prefeituras Homologadas</span>
            <span className="text-2xs text-purple-600 font-semibold block mt-0.5">Padrão ABRASF / Nacional</span>
          </div>
        </div>
      </div>

      {/* Tabela Mestre de Empresas Emissoras */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-4.5 h-4.5 text-red-600" />
              Tabela de Estabelecimentos Emissores (Multi-CNPJ)
            </h3>
            <p className="text-2xs text-slate-500 mt-0.5">
              Empresas configuradas para emissão com alíquotas de ICMS/ISS e certificados digitais vinculados
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleTestarSefaz()}
              disabled={testandoSefaz}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-xl text-xs font-bold cursor-pointer transition-colors disabled:opacity-50"
              title="Testar Conexão com SEFAZ / Focus NFe"
            >
              {testandoSefaz ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-emerald-600" />
              ) : (
                <Zap className="w-3.5 h-3.5 text-emerald-600" />
              )}
              {testandoSefaz ? 'Testando SEFAZ...' : 'Testar Comunicação SEFAZ'}
            </button>
            <button
              onClick={carregarEmpresas}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 cursor-pointer transition-colors"
              title="Recarregar"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleNovaEmpresa}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold cursor-pointer transition-colors"
            >
              <Plus className="w-4 h-4 text-red-400" />
              Nova Empresa Emissora
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-700">
            <thead className="bg-slate-50 border-b border-slate-200 text-2xs uppercase text-slate-500 font-bold">
              <tr>
                <th className="px-4 py-3">Empresa / Razão Social</th>
                <th className="px-4 py-3">CNPJ / Inscrições</th>
                <th className="px-4 py-3">Regime Tributário</th>
                <th className="px-4 py-3">Certificado Digital A1</th>
                <th className="px-4 py-3">Alíquotas Padrão</th>
                <th className="px-4 py-3">Ambiente</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-red-500" />
                    Carregando empresas emissoras...
                  </td>
                </tr>
              ) : empresas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="font-semibold text-slate-600">Nenhuma empresa emissora cadastrada.</p>
                    <button
                      onClick={handleNovaEmpresa}
                      className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold"
                    >
                      <Plus className="w-4 h-4" /> Cadastrar Primeira Empresa
                    </button>
                  </td>
                </tr>
              ) : (
                empresas.map((emp) => (
                  <tr key={emp.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-3.5 font-semibold text-slate-900">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-red-50 text-red-600 flex items-center justify-center font-bold text-2xs">
                          {emp.id}
                        </div>
                        <div>
                          <div className="font-bold text-slate-900">{emp.razao_social}</div>
                          {emp.nome_fantasia && (
                            <div className="text-2xs font-normal text-slate-400">{emp.nome_fantasia}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 font-mono text-xs text-slate-700">
                      <div className="font-semibold">{emp.cnpj}</div>
                      <div className="text-3xs text-slate-400 flex items-center gap-2 mt-0.5 font-sans">
                        <span>IE: {emp.inscricao_estadual || 'Isento'}</span>
                        {emp.rntrc && <span>RNTRC: {emp.rntrc}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="px-2.5 py-1 rounded-md text-2xs font-bold bg-slate-100 text-slate-700 uppercase tracking-tight">
                        {emp.regime_tributario === 'simples_nacional'
                          ? 'Simples Nacional'
                          : emp.regime_tributario === 'lucro_presumido'
                          ? 'Lucro Presumido'
                          : 'Lucro Real'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      {emp.certificado ? (
                        <div className="space-y-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-3xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <ShieldCheck className="w-3 h-3 text-emerald-600" />
                            Válido até {emp.certificado.validade_fim ? new Date(emp.certificado.validade_fim).toLocaleDateString('pt-BR') : '2027'}
                          </span>
                          <div className="text-3xs text-slate-400 truncate max-w-[150px]" title={emp.certificado.nome_arquivo}>
                            {emp.certificado.nome_arquivo}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleAbrirUploadCertificado(emp)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-2xs font-bold bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition-colors cursor-pointer"
                        >
                          <UploadCloud className="w-3.5 h-3.5 text-amber-600" />
                          Subir Certificado A1
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="space-y-0.5 text-2xs">
                        <div className="text-slate-700">
                          <span className="text-slate-400 font-mono">ICMS:</span>{' '}
                          <strong className="text-slate-900">{emp.aliquota_icms ?? 0}%</strong>
                        </div>
                        <div className="text-slate-700">
                          <span className="text-slate-400 font-mono">ISS:</span>{' '}
                          <strong className="text-slate-900">{emp.aliquota_iss ?? 2.0}%</strong>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        className={`px-2 py-0.5 rounded text-2xs font-bold uppercase tracking-tight ${
                          emp.ambiente_emissao === 'producao'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {emp.ambiente_emissao === 'producao' ? 'Produção' : 'Homologação'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => handleTestarSefaz(emp)}
                          className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors cursor-pointer"
                          title="Testar Conexão SEFAZ desta Empresa"
                        >
                          <Zap className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleAbrirUploadCertificado(emp)}
                          className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors cursor-pointer"
                          title="Gerenciar Certificado A1"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditarEmpresa(emp)}
                          className="p-1.5 hover:bg-slate-100 text-slate-700 rounded-lg transition-colors cursor-pointer"
                          title="Editar Parâmetros & Alíquotas"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleExcluirEmpresa(emp.id, emp.razao_social)}
                          className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors cursor-pointer"
                          title="Excluir Empresa"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Completo de Cadastro / Edição da Empresa Emissora */}
      {modalEmpresaAberto && empresaEmEdicao && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
            {/* Topo do Modal */}
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-red-600" />
                  {empresaEmEdicao.id ? 'Editar Empresa Emissora' : 'Cadastrar Nova Empresa Emissora'}
                </h3>
                <p className="text-2xs text-slate-500 mt-0.5">
                  Preencha os dados cadastrais, defina as alíquotas tributárias e gerencie a emissão fiscal.
                </p>
              </div>
              <button
                onClick={() => setModalEmpresaAberto(false)}
                className="p-1.5 hover:bg-slate-200 text-slate-500 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Abas do Modal */}
            <div className="flex border-b border-slate-200 bg-slate-100/70 px-4 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setAbaAtiva('geral')}
                className={`py-3 px-4 border-b-2 font-bold cursor-pointer transition-colors flex items-center gap-2 ${
                  abaAtiva === 'geral'
                    ? 'border-red-600 text-red-600 bg-white'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Building2 className="w-4 h-4" /> Dados Cadastrais
              </button>
              <button
                type="button"
                onClick={() => setAbaAtiva('tributacao')}
                className={`py-3 px-4 border-b-2 font-bold cursor-pointer transition-colors flex items-center gap-2 ${
                  abaAtiva === 'tributacao'
                    ? 'border-red-600 text-red-600 bg-white'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Percent className="w-4 h-4" /> Alíquotas & Impostos
              </button>
              <button
                type="button"
                onClick={() => setAbaAtiva('series')}
                className={`py-3 px-4 border-b-2 font-bold cursor-pointer transition-colors flex items-center gap-2 ${
                  abaAtiva === 'series'
                    ? 'border-red-600 text-red-600 bg-white'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Layers className="w-4 h-4" /> Séries & Numeração
              </button>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSalvarEmpresa} className="flex-1 overflow-y-auto p-6 space-y-5">
              {abaAtiva === 'geral' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        Razão Social *
                      </label>
                      <input
                        type="text"
                        required
                        value={empresaEmEdicao.razao_social || ''}
                        onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, razao_social: e.target.value })}
                        placeholder="Ex: R.G DA SILVA TRANSPORTES LTDA"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-hidden focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        Nome Fantasia
                      </label>
                      <input
                        type="text"
                        value={empresaEmEdicao.nome_fantasia || ''}
                        onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, nome_fantasia: e.target.value })}
                        placeholder="Ex: VOOPE TRANSPORTES"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-hidden focus:border-red-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        CNPJ *
                      </label>
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          required
                          value={empresaEmEdicao.cnpj || ''}
                          onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, cnpj: e.target.value })}
                          placeholder="00.000.000/0000-00"
                          className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-semibold focus:outline-hidden focus:border-red-500"
                        />
                        <button
                          type="button"
                          onClick={handleBuscarCnpj}
                          disabled={buscandoCnpj}
                          title="Buscar dados na Receita Federal (via Focus NFe)"
                          className="shrink-0 px-2.5 bg-slate-900 hover:bg-black text-white rounded-xl transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {buscandoCnpj ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Search className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        Inscrição Estadual (IE)
                      </label>
                      <input
                        type="text"
                        value={empresaEmEdicao.inscricao_estadual || ''}
                        onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, inscricao_estadual: e.target.value })}
                        placeholder="Número da IE"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono focus:outline-hidden focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        Inscrição Municipal (IM)
                      </label>
                      <input
                        type="text"
                        value={empresaEmEdicao.inscricao_municipal || ''}
                        onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, inscricao_municipal: e.target.value })}
                        placeholder="Número da IM"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono focus:outline-hidden focus:border-red-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        RNTRC (ANTT)
                      </label>
                      <input
                        type="text"
                        value={empresaEmEdicao.rntrc || ''}
                        onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, rntrc: e.target.value })}
                        placeholder="Registro ANTT"
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono focus:outline-hidden focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        Regime Tributário (CRT)
                      </label>
                      <select
                        value={empresaEmEdicao.regime_tributario || 'simples_nacional'}
                        onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, regime_tributario: e.target.value as RegimeTributario })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-hidden focus:border-red-500"
                      >
                        <option value="simples_nacional">1 - Simples Nacional</option>
                        <option value="lucro_presumido">3 - Lucro Presumido</option>
                        <option value="lucro_real">3 - Lucro Real</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                        Ambiente de Emissão
                      </label>
                      <select
                        value={empresaEmEdicao.ambiente_emissao || 'homologacao'}
                        onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, ambiente_emissao: e.target.value as AmbienteFiscal })}
                        className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-hidden focus:border-red-500"
                      >
                        <option value="homologacao">Homologação (Ambiente de Testes)</option>
                        <option value="producao">Produção (Validade Jurídica)</option>
                      </select>
                    </div>
                  </div>

                  <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-xl">
                    <MunicipioUfSelect
                      label="Município de Registro (para emissão de NFS-e)"
                      required
                      value={{
                        municipio: empresaEmEdicao.municipio || '',
                        uf: empresaEmEdicao.uf || '',
                        codigoIbge: empresaEmEdicao.codigo_municipio_ibge || '',
                      }}
                      onChange={(v) =>
                        setEmpresaEmEdicao({
                          ...empresaEmEdicao,
                          municipio: v.municipio,
                          uf: v.uf,
                          codigo_municipio_ibge: v.codigoIbge,
                        })
                      }
                    />
                    <p className="text-3xs text-blue-600 mt-1.5">
                      Município onde a empresa está registrada na prefeitura — usado como código IBGE obrigatório na emissão de NFS-e.
                    </p>
                  </div>
                </div>
              )}

              {abaAtiva === 'tributacao' && (
                <div className="space-y-6">
                  {/* Seção CT-e */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-red-600" />
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                        Tributação do Transporte de Cargas (CT-e)
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-3xs font-bold text-slate-600 uppercase mb-1">
                          Alíquota ICMS Padrão (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={empresaEmEdicao.aliquota_icms ?? 0}
                          onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, aliquota_icms: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900"
                        />
                        <span className="text-3xs text-slate-400 mt-0.5 block">0% para Simples Nacional</span>
                      </div>
                      <div>
                        <label className="block text-3xs font-bold text-slate-600 uppercase mb-1">
                          CFOP Padrão
                        </label>
                        <CfopAutocomplete
                          value={empresaEmEdicao.cfop_padrao || '5353'}
                          onChange={(codigo) => setEmpresaEmEdicao({ ...empresaEmEdicao, cfop_padrao: codigo })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-900"
                        />
                        <span className="text-3xs text-slate-400 mt-0.5 block">Ex: 5353 Estadual, 6353 Interestadual</span>
                      </div>
                      <div>
                        <label className="block text-3xs font-bold text-slate-600 uppercase mb-1">
                          CST / CSOSN ICMS
                        </label>
                        <input
                          type="text"
                          value={empresaEmEdicao.cst_icms_padrao || '102'}
                          onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, cst_icms_padrao: e.target.value })}
                          placeholder="102"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-900"
                        />
                        <span className="text-3xs text-slate-400 mt-0.5 block">Simples: 101/102 | Regime Normal: 00/20</span>
                      </div>
                    </div>
                  </div>

                  {/* Seção NFS-e */}
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-purple-600" />
                      <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                        Tributação de Serviços Municipais (NFS-e)
                      </h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-3xs font-bold text-slate-600 uppercase mb-1">
                          Alíquota ISS (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={empresaEmEdicao.aliquota_iss ?? 2.0}
                          onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, aliquota_iss: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900"
                        />
                        <span className="text-3xs text-slate-400 mt-0.5 block">Padrão municipal: 2.00% a 5.00%</span>
                      </div>
                      <div>
                        <label className="block text-3xs font-bold text-slate-600 uppercase mb-1">
                          PIS Retido (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={empresaEmEdicao.aliquota_pis ?? 0}
                          onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, aliquota_pis: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-3xs font-bold text-slate-600 uppercase mb-1">
                          COFINS Retido (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={empresaEmEdicao.aliquota_cofins ?? 0}
                          onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, aliquota_cofins: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {abaAtiva === 'series' && (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                      Numeração Sequencial de Documentos
                    </h4>
                    <p className="text-2xs text-slate-500">
                      Defina a série e o próximo número a ser gerado para evitar pulos ou duplicidade junto à SEFAZ e Prefeitura.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2">
                        <span className="text-xs font-bold text-red-600 flex items-center gap-1.5">
                          <Truck className="w-4 h-4" /> CT-e (Modelo 57)
                        </span>
                        <div>
                          <label className="block text-3xs font-bold text-slate-500 uppercase">Série</label>
                          <input
                            type="text"
                            value={empresaEmEdicao.serie_cte || '1'}
                            onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, serie_cte: e.target.value })}
                            className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-3xs font-bold text-slate-500 uppercase">Próximo Número</label>
                          <input
                            type="number"
                            value={empresaEmEdicao.proximo_numero_cte ?? 1}
                            onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, proximo_numero_cte: parseInt(e.target.value) || 1 })}
                            className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-900"
                          />
                        </div>
                      </div>

                      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2">
                        <span className="text-xs font-bold text-purple-600 flex items-center gap-1.5">
                          <Receipt className="w-4 h-4" /> NFS-e Municipal
                        </span>
                        <div>
                          <label className="block text-3xs font-bold text-slate-500 uppercase">Série RPS</label>
                          <input
                            type="text"
                            value={empresaEmEdicao.serie_nfse || '1'}
                            onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, serie_nfse: e.target.value })}
                            className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-3xs font-bold text-slate-500 uppercase">Próximo Número RPS</label>
                          <input
                            type="number"
                            value={empresaEmEdicao.proximo_numero_nfse ?? 1}
                            onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, proximo_numero_nfse: parseInt(e.target.value) || 1 })}
                            className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-900"
                          />
                        </div>
                      </div>

                      <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2">
                        <span className="text-xs font-bold text-blue-600 flex items-center gap-1.5">
                          <FileSpreadsheet className="w-4 h-4" /> MDF-e (Modelo 58)
                        </span>
                        <div>
                          <label className="block text-3xs font-bold text-slate-500 uppercase">Série</label>
                          <input
                            type="text"
                            value={empresaEmEdicao.serie_mdfe || '1'}
                            onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, serie_mdfe: e.target.value })}
                            className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono"
                          />
                        </div>
                        <div>
                          <label className="block text-3xs font-bold text-slate-500 uppercase">Próximo Número</label>
                          <input
                            type="number"
                            value={empresaEmEdicao.proximo_numero_mdfe ?? 1}
                            onChange={(e) => setEmpresaEmEdicao({ ...empresaEmEdicao, proximo_numero_mdfe: parseInt(e.target.value) || 1 })}
                            className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono font-bold text-slate-900"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Rodapé de Ações do Modal */}
              <div className="pt-4 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalEmpresaAberto(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-md shadow-red-900/20 cursor-pointer transition-all flex items-center gap-2"
                >
                  {salvando ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" /> Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Salvar Configurações
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Dedicado para Upload do Certificado Digital A1 */}
      {modalCertificadoAberto && empresaSelecionadaCertificado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                  Vincular Certificado Digital A1
                </h3>
                <p className="text-2xs text-slate-500 mt-0.5">
                  {empresaSelecionadaCertificado.razao_social} (CNPJ: {empresaSelecionadaCertificado.cnpj})
                </p>
              </div>
              <button
                onClick={() => setModalCertificadoAberto(false)}
                className="p-1.5 hover:bg-slate-200 text-slate-500 rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEnviarCertificado} className="p-6 space-y-4">
              {/* Informação do certificado atual se houver */}
              {empresaSelecionadaCertificado.certificado && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-2 text-emerald-800 text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <div>
                      <div>Certificado atual instalado</div>
                      <div className="text-3xs text-emerald-600 font-normal">
                        Arquivo: {empresaSelecionadaCertificado.certificado.nome_arquivo}
                      </div>
                    </div>
                  </div>
                  <span className="text-3xs font-bold uppercase text-emerald-700 bg-white px-2 py-0.5 rounded-full border border-emerald-200">
                    Válido
                  </span>
                </div>
              )}

              {/* Campo de Seleção do Arquivo */}
              <div>
                <label className="block text-2xs font-bold text-slate-700 uppercase mb-1">
                  Arquivo do Certificado (.pfx ou .p12) *
                </label>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-200 hover:border-blue-500 hover:bg-blue-50/40 rounded-2xl p-6 text-center cursor-pointer transition-all"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pfx,.p12"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setArquivoCertificado(e.target.files[0]);
                      }
                    }}
                  />
                  <UploadCloud className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  {arquivoCertificado ? (
                    <div>
                      <p className="text-xs font-bold text-slate-900">{arquivoCertificado.name}</p>
                      <p className="text-3xs text-slate-400 mt-0.5">
                        {(arquivoCertificado.size / 1024).toFixed(1)} KB - Clique para trocar de arquivo
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs font-bold text-slate-700">Clique para selecionar o Certificado A1</p>
                      <p className="text-3xs text-slate-400 mt-0.5">Formatos suportados: .pfx ou .p12</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Campo da Senha */}
              <div>
                <label className="block text-2xs font-bold text-slate-700 uppercase mb-1 flex items-center gap-1">
                  <Lock className="w-3.5 h-3.5 text-slate-400" /> Senha do Certificado Digital *
                </label>
                <input
                  type="password"
                  required
                  value={senhaCertificado}
                  onChange={(e) => setSenhaCertificado(e.target.value)}
                  placeholder="Digite a senha de proteção do arquivo"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:outline-hidden focus:border-blue-500"
                />
                <p className="text-3xs text-slate-400 mt-1">
                  A senha é utilizada para criptografia e validação da chave privada na emissão.
                </p>
              </div>

              {/* Ações */}
              <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalCertificadoAberto(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold cursor-pointer transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={enviandoCertificado || !arquivoCertificado || !senhaCertificado}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-900/20 cursor-pointer transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {enviandoCertificado ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" /> Processando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" /> Instalar Certificado A1
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Modal de Resultado do Teste de Comunicação com SEFAZ / Focus NFe */}
      {modalResultadoTeste && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95">
            <div className={`p-5 border-b flex items-center justify-between text-white ${
              resultadoTeste?.sucesso ? 'bg-slate-900' : 'bg-red-950'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  resultadoTeste?.sucesso ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {resultadoTeste?.sucesso ? <Zap className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                </div>
                <div>
                  <h3 className="text-sm font-bold">Diagnóstico de Conexão SEFAZ</h3>
                  <p className="text-2xs text-slate-300">
                    Provedor: Focus NFe v2 • {resultadoTeste?.ambiente === 'producao' ? 'Produção' : 'Homologação'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setModalResultadoTeste(false)}
                className="p-1.5 hover:bg-white/10 rounded-lg text-slate-300 hover:text-white cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className={`p-3.5 rounded-xl border flex items-start gap-3 ${
                resultadoTeste?.sucesso
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-red-50 border-red-200 text-red-900'
              }`}>
                {resultadoTeste?.sucesso ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                )}
                <div>
                  <h4 className="font-bold text-xs">{resultadoTeste?.mensagem}</h4>
                  <p className="text-2xs mt-0.5 opacity-90">
                    {resultadoTeste?.sucesso
                      ? 'O Supabase comunicou-se com sucesso com os servidores da Focus NFe e SEFAZ.'
                      : 'Verifique se a secret FOCUS_NFE_API_TOKEN está configurada corretamente no Supabase.'}
                  </p>
                </div>
              </div>

              {/* Status do Diagnóstico */}
              <div className="space-y-2 bg-slate-50 p-4 rounded-xl border border-slate-200 font-medium">
                <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                  <span className="text-slate-500 text-2xs">Empresa Testada:</span>
                  <span className="text-slate-900 font-bold">{resultadoTeste?.empresa}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                  <span className="text-slate-500 text-2xs">Ambiente SEFAZ:</span>
                  <span className="px-2 py-0.5 rounded text-3xs font-bold uppercase bg-amber-100 text-amber-800">
                    {resultadoTeste?.ambiente || 'Homologação'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                  <span className="text-slate-500 text-2xs">API Focus NFe v2:</span>
                  <span className="text-emerald-700 font-bold flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Online & Respondendo
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200/60">
                  <span className="text-slate-500 text-2xs">Certificado Digital A1:</span>
                  <span className="text-slate-900 font-semibold">{resultadoTeste?.status_certificado || 'Verificado'}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-slate-500 text-2xs">Prontidão para Emissão:</span>
                  <span className="text-slate-900 font-semibold">{resultadoTeste?.status_sefaz || 'Habilitado'}</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setModalResultadoTeste(false)}
                className="px-5 py-2 bg-slate-900 hover:bg-black text-white text-xs font-bold rounded-xl cursor-pointer transition-colors"
              >
                Fechar Diagnóstico
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
